import "./App.css";
import React, { useState, useEffect, useRef } from "react";
import Welcome from "./components/Welcome";
import Header from "./components/Header";
import axios from "axios";
import RecipeGrid from "./components/RecipeGrid";
import RecipeDetail from "./components/RecipeDetail";
import useApiRequest from "./hooks/useApiRequest";

import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";

const App = () => {
  const [validCredentials, setValidCredentials] = useState(true);
  const [recipes, setRecipes] = useState([]);
  const [lastScrollPosition, setLastScrollPosition] = useState(0);
  const nextUrlRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const initialRequestStartedRef = useRef(false);
  const hasSearchedRef = useRef(false);
  const requestGenerationRef = useRef(0);
  const { makeApiRequest } = useApiRequest();

  const updateNextUrl = (url) => {
    nextUrlRef.current = url;
  };

  const mergeRecipes = (current, incoming) => {
    const recipesById = new Map(current.map((recipe) => [recipe.id, recipe]));
    incoming.forEach((recipe) => recipesById.set(recipe.id, recipe));
    return Array.from(recipesById.values());
  };

  const scrollToLastPosition = () => {
    requestAnimationFrame(() => {
      window.scrollTo(0, lastScrollPosition);
    });
  };

  // New component that uses useNavigate
  const RecipeSelector = () => {
    const navigate = useNavigate();

    const handleRecipeSelect = async (recipeId) => {
      setLastScrollPosition(window.scrollY);
      navigate(`/recipe/${recipeId}`);
      window.scrollTo(0, 0);
    };

    return <RecipeGrid recipes={recipes} onRecipeSelect={handleRecipeSelect} />;
  };

  // In your React component or a useEffect hook
  useEffect(() => {
    if (initialRequestStartedRef.current) return;
    initialRequestStartedRef.current = true;
    const storedUrl = localStorage.getItem("tandoorUrl") || "";
    const storedToken = localStorage.getItem("apiToken") || "";

    const browserCredentials =
      storedUrl && storedToken
        ? { baseUrl: storedUrl, apiToken: storedToken }
        : {};

    console.log("Checking configured credentials...");
    axios
      .post("/api/proxy", {
        method: "GET",
        endpoint: "/api/recipe/",
        ...browserCredentials,
      })
      .then((response) => {
        console.log("Configured credentials work");
        setValidCredentials(true);
        setRecipes(response.data.results || []);
        updateNextUrl(response.data.next);
      })
      .catch((error) => {
        console.log("Error fetching recipes with configured credentials:", error);
        setValidCredentials(false);
      });
  }, []);

  const handleSaveCredentials = (url, token) => {
    localStorage.setItem("tandoorUrl", url);
    localStorage.setItem("apiToken", token);

    axios.post("/api/proxy", {
      method: "GET",
      endpoint: "/api/recipe/",
      baseUrl: url,
      apiToken: token,
    })
      .then((response) => {
        setValidCredentials(true);
        ++requestGenerationRef.current;
        setRecipes(response.data.results || []);
        updateNextUrl(response.data.next);
      })
      .catch((error) => {
        // Handle error
        console.error("Error fetching recipes with local credentials:", error);
        setValidCredentials(false);
      });
    // Validation completes asynchronously in the request handlers above.
    return true;
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const [searchTerm, setSearchTerm] = useState("");

  // Custom hook for debouncing
  function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
  }

  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500 ms delay

  useEffect(() => {
    if (debouncedSearchTerm.length === 0 && !hasSearchedRef.current) return;
    hasSearchedRef.current = true;
    const requestGeneration = ++requestGenerationRef.current;
    setRecipes([]);
    updateNextUrl(null);
    const endpoint =
      debouncedSearchTerm.length > 0
        ? `/api/recipe/?query=${encodeURIComponent(debouncedSearchTerm)}`
        : "/api/recipe/";
    makeApiRequest(endpoint, "GET")
      .then((response) => {
        if (requestGeneration !== requestGenerationRef.current) return;
        setRecipes(response.data.results || []);
        updateNextUrl(response.data.next);
      })
      .catch((error) => {
        console.error(`Error fetching recipes:`, error);
      });
  }, [debouncedSearchTerm, makeApiRequest]);

  useEffect(() => {
    const fetchMoreRecipes = async () => {
      const url = nextUrlRef.current;
      if (!url || loadingMoreRef.current) return;
      loadingMoreRef.current = true;
      const requestGeneration = requestGenerationRef.current;
      try {
        const urlObject = new URL(url, window.location.origin);
        const response = await makeApiRequest(
          `${urlObject.pathname}${urlObject.search}`,
          "GET"
        );
        if (requestGeneration !== requestGenerationRef.current) return;
        setRecipes((current) =>
          mergeRecipes(current, response.data.results || [])
        );
        updateNextUrl(response.data.next);
      } catch (error) {
        console.error("Error fetching more recipes:", error);
      } finally {
        loadingMoreRef.current = false;
      }
    };

    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 100
      ) {
        fetchMoreRecipes();
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [makeApiRequest]);

  return (
    <Router>
      <div className="App">
        {!validCredentials ? (
          <Welcome
            onSaveCredentials={handleSaveCredentials}
            validCredentials={validCredentials}
          />
        ) : (
          <>
            <Header
              searchTerm={searchTerm}
              handleSearchChange={handleSearchChange}
              scrollToLastPosition={scrollToLastPosition}
            />
            <Routes>
              <Route
                path="/recipe/:id/:action/:step"
                element={<RecipeDetail />}
              />

              <Route
                path="/recipe/:id"
                element={
                  <RecipeDetail scrollToLastPosition={scrollToLastPosition} />
                }
              />

              <Route path="/" element={<RecipeSelector />} />

              {/* Fallback for any other path */}
              <Route path="*" element={<div>404 Not Found</div>} />
            </Routes>
          </>
        )}
      </div>
    </Router>
  );
};

export default App;
